import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ErrorService } from '@app/services/error.service';
import { OAuth2Service } from '@app/services/oauth2.service';
import { SessionService } from '@app/services/session/session.service';
import { environment } from '@env/environment';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { LocalStorageService } from 'ngx-webstorage';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: false,
})
export class LoginPageComponent implements OnInit {
  loading = false;
  logged = false;

  constructor(
    private sessionService: SessionService,
    private localStorageService: LocalStorageService,
    private oAuth2Service: OAuth2Service,
    private toasterService: ToastrService,
    private translateService: TranslateService,
    private errorService: ErrorService,
    private activatedRoute: ActivatedRoute
  ) {}

  errorStatus: string = '';

  ngOnInit() {
    // Controllo se siamo in ambiente di sviluppo
    const isDevelopment = environment.production === false;

    // Se non siamo in sviluppo e c'è una sessione, facciamo il logout
    if (!isDevelopment && this.localStorageService.retrieve('session')) {
      this.sessionService.logoutNoSession();
    }

    const identity = localStorage.getItem('identity');
    if (identity) {
      // Evita loop infiniti di auto-login senza consumare il flag identity:
      // il redirect a /renew viene gestito in AppComponent dopo l'atterraggio su /homepage.
      const lastIdentityAutoLogin = sessionStorage.getItem(
        'identityAutoLoginValue'
      );
      if (lastIdentityAutoLogin !== identity) {
        sessionStorage.setItem('identityAutoLoginValue', identity);
        // l'utente sta avviando un nuovo login esplicito
        sessionStorage.removeItem('blockAutoLoginAfterLogout');
        this.oAuth2Service.login();
      }
    }
  }

  ngAfterViewInit() {
    this.checkQueryParams();
  }

  checkQueryParams() {
    // Recupera l'URL corrente
    //http://localhost:4200/?error=access_denied&error_description=Authentication+was+canceled#/login
    const currentUrl = window.location.href;

    // Definisce una regex per intercettare path e query string
    const regex = /\/([^?#]+)(?:\?([^#]+))?/;

    // Usa la regex per estrarre path e parametri di query
    const match = currentUrl.match(regex);

    if (match) {
      const path = match[1];
      const queryParameters = match[2] ? match[2] : '';

      // Da qui elaboriamo path e parametri estratti
      console.log('Path:', path);
      console.log('Query Parameters:', queryParameters);
      // Divide la query string in coppie chiave=valore
      const paramPairs = queryParameters.split('&');

      // Crea un oggetto per memorizzare i parametri
      const queryParams = {};

      // Itera sulle coppie e separa chiave e valore
      for (const pair of paramPairs) {
        const [key, value] = pair.split('=');

        // Verifica che il valore esista prima della sostituzione
        if (value) {
          // Decodifica il valore per gestire i caratteri URL-encoded
          queryParams[key] = decodeURIComponent(value.replace(/\+/g, ' '));
        }
      }

      const protocolErrors = [
        'invalid_request',
        'unauthorized_client',
        'unsupported_response_type',
        'invalid_scope',
        'temporarily_unavailable',
        'request_not_supported',
        'request_uri_not_supported',
        'registration_not_supported',
      ];
      const authErrors = [
        'server_error',
        'account_selection_required',
        'interaction_required',
        'login_required',
      ];
      const tokenErrors = [
        'invalid_request',
        'unauthorized_client',
        'invalid_grant',
        'unsupported_grant_type',
      ];

      if (queryParams['error'] && queryParams['error_description']) {
        if (
          protocolErrors.includes(queryParams['error']) ||
          authErrors.includes(queryParams['error']) ||
          tokenErrors.includes(queryParams['error'])
        ) {
          window.location.href =
            window.location.href.split('?')[0] + '/#/error';
        } else {
          switch (queryParams['error_description']) {
            case 'Account is disabled':
              this.errorStatus = 'DISABLED';
              this.showModal();
              break;
            case 'Authentication was canceled':
              //apro la modale d'errore con l'errore segnalato
              this.errorService.openErrorModal(
                queryParams['error_description']
              );

              // clear query params after error message (to avoid repeating message in refresh)
              setTimeout(() => {
                window.location.href =
                  window.location.href.split('?')[0] + '/#/login';
              }, 5000);
              break;
            case 'No multi-factor authentication enabled for account':
              this.errorStatus = 'NO_MULTIFACTOR';
              this.showModal();
              break;
            case 'Account password is locked':
              this.errorStatus = 'LOCKED';
              this.showModal();
              break;
            case 'Authentication has timed out':
              this.errorStatus = 'TIMED_OUT';
              this.showModal();
              break;
            case 'Too many failed authentication attempts':
              this.errorStatus = 'FAILED_AUTH_ATTEMPTS';
              this.showModal();
              break;
            default:
              window.location.href =
                window.location.href.split('?')[0] + '/#/error';
              break;
          }
        }
        /*
            // toast error with error_description
            this.toasterService.error(
                queryParams['error_description'],
                '',
                {
                positionClass: 'toast-top-right',
                }
            );
            // clear query params after error message (to avoid repeating message in refresh)
            setTimeout(() => {
                window.location.href = window.location.href.split('?')[0] + '/#/login';
            }, 5000); */
      }
    }
  }

  onSubmit() {
    // nuovo tentativo di accesso esplicito
    sessionStorage.removeItem('blockAutoLoginAfterLogout');
    this.oAuth2Service.login();
  }

  goToPasswordRecovery() {
    //console.log('location', window.location.hostname);
    const recoveryUrl =
      window.location.hostname === 'mysign.infocert.it'
        ? 'https://idp.infocert.it/recovery' // production
        : 'https://idpcl.infocert.it/recovery'; // collaudo or local

    window.open(recoveryUrl, '_blank');
  }

  public authErrorsModalVisible = false;

  showModal() {
    this.authErrorsModalVisible = true;
  }

  onToggleAuthErrorsModal(visible: boolean): void {
    this.authErrorsModalVisible = visible;
    if (!visible) {
      this.closeModal();
    }
  }

  closeModal() {
    this.authErrorsModalVisible = false;
    window.location.href = window.location.href.split('?')[0] + '/#/login';
  }

  openUrl(type: string) {
    switch (type) {
      case 'support':
        window.open(
          this.translateService.instant('URLS.CONTACT_SUPPORT_URL'),
          '_blank'
        );
        break;
      case 'myInfoCert':
        window.open(this.translateService.instant('URLS.MYINFOCERT'), '_blank');
        break;
    }
  }
}
